"use client";

import Link from "next/link";
import { useClimateIndex, useClimateState } from "./use-climate-state";
import { ShareTools } from "./share-tools";
import {
  ELEVATIONS,
  REGIONAL_SKI_RISK,
  SKI_RISK,
  type SourceKey,
} from "@/lib/climate/evidence";
import { DataTable, RangeChart, SectionTitle, Segments, Sources } from "./ui";
import s from "./climate.module.css";

const REGIONS: {
  value: string;
  label: string;
  title: string;
  text: string;
  source: SourceKey;
}[] = [
  {
    value: "alps",
    label: "Alps & Pyrenees",
    title: "Altitude buys time, unevenly.",
    text: "Lower slopes lose snow reliability first. The southern Alps and Pyrenees need more elevation than the northern Alps to meet the same modelled natural-snow criterion. More dependable high terrain does not protect lower links and resort return runs.",
    source: "elevation",
  },
  {
    value: "rockies",
    label: "North America",
    title: "Cold, high and snowy retains an advantage.",
    text: "A September 2026 Rocky Mountain study finds high-elevation, historically snowy resorts are less affected over the next few decades. Colorado resorts generally retain more snow security than resorts closer to the Pacific coast, despite stronger warming. This is a regional result, not a ranking of every North American resort.",
    source: "rockies",
  },
  {
    value: "scandinavia",
    label: "Scandinavia",
    title: "Latitude helps, but coastal exposure matters.",
    text: "Higher latitude can delay snow reliability losses. Low and maritime resorts remain exposed to rain replacing snow, and a colder regional climate does not eliminate shorter seasons under continued warming.",
    source: "mountains",
  },
  {
    value: "japan",
    label: "Japan",
    title: "Big snowfalls can coexist with a shrinking season.",
    text: "The IPCC assessment identifies substantial late-century snow-reliability risks in Japan under high emissions. Colder and higher locations can fare better, but heavy individual storms do not cancel a decline in the length of snow cover.",
    source: "international",
  },
];
const ALTITUDES = [
  {
    value: "low",
    label: "Below 1,500 m",
    title: "Opening weeks get harder to count on.",
    text: "At lower Alpine elevations, a warmer atmosphere more often turns precipitation into rain and speeds melting. Snowmaking needs cold conditions and available water.",
  },
  {
    value: "mid",
    label: "1,500–2,000 m",
    title: "The edges of the season retreat.",
    text: "A viable core winter can coexist with less dependable early and late snow. Aspect, local climate and slope management matter alongside altitude.",
  },
  {
    value: "high",
    label: "Above 2,000 m",
    title: "Greater resilience, with new pressures.",
    text: "Higher terrain generally holds snow longer. Retreating glaciers and thawing mountain permafrost can still affect lifts, routes and slope stability.",
  },
];

function SkiOutlook() {
  const [snowmaking, setSnowmaking] = useClimateState("snowmaking", "natural", [
    "natural",
    "snowmaking",
  ]);
  const [region, setRegion] = useClimateState(
    "skiRegion",
    "alps",
    REGIONS.map((r) => r.value),
  );
  const [altitude, setAltitude] = useClimateState(
    "altitude",
    "mid",
    ALTITUDES.map((r) => r.value),
  );
  const [elevation, setElevation] = useClimateIndex(
    "elevation",
    0,
    ELEVATIONS.length,
  );
  const currentRegion = REGIONS.find((r) => r.value === region)!;
  const currentAltitude = ALTITUDES.find((a) => a.value === altitude)!;
  return (
    <>
      <div className={s.twoCol}>
        <article className={s.panel}>
          <h3>Does skiing have a future?</h3>
          <p className={s.small}>
            Yes, but where, how long and at what cost changes with warming.
            Europe’s snow-supply risk rises sharply.
          </p>
          <Segments
            label="Ski adaptation comparison"
            value={snowmaking}
            onChange={setSnowmaking}
            options={[
              { value: "natural", label: "Without snowmaking" },
              { value: "snowmaking", label: "Snowmaking on 50% of slopes" },
            ]}
          />
          {SKI_RISK.map((row) => (
            <div className={s.riskRow} key={row.name}>
              <div className={s.riskLabel}>
                <span>{row.name}</span>
                <strong>{row[snowmaking]}%</strong>
              </div>
              <div
                className={s.riskTrack}
                role="img"
                aria-label={`${row.name}: ${row[snowmaking]} per cent of resorts at very high snow-supply risk`}
              >
                <div
                  className={s.riskBar}
                  data-adapted={snowmaking === "snowmaking"}
                  style={{ width: `${row[snowmaking]}%` }}
                />
              </div>
            </div>
          ))}
          <div className={s.riskAxis} aria-hidden="true"><span>0%</span><span>50%</span><span>100% of resorts</span></div>
          <div className={s.sportTakeaway} aria-live="polite">
            <strong>{snowmaking === "snowmaking" ? "Adaptation helps. Warming still matters." : "Snow reliability, not a closure forecast."}</strong>
            <p>
              {snowmaking === "snowmaking"
                ? `At +2°C, the modelled share at very high risk falls from ${SKI_RISK[0].natural}% to ${SKI_RISK[0].snowmaking}%. At +4°C, ${SKI_RISK[1].snowmaking}% remain at very high risk even with snowmaking.`
                : "For skiing, the pressure falls on dependable snow and the length of the season. Resort closures also depend on local terrain, adaptation and business decisions."}
            </p>
          </div>
          <p className={s.small}>
            Share of 2,234 European resorts at very high snow-supply risk.
            Global warming levels relative to pre-industrial conditions, not
            particular calendar years.
          </p>
          <DataTable
            headers={["Warming", "No snowmaking", "50% snowmaking"]}
            rows={SKI_RISK.map((r) => [
              `+${r.warming}°C`,
              `${r.natural}%`,
              `${r.snowmaking}%`,
            ])}
          />
          <Sources ids={["ski"]} />
          <p className={s.note} aria-live="polite">
            {snowmaking === "snowmaking"
              ? "Snowmaking reduces risk but cannot restore cold weather. The study assumes coverage on half the slopes, with extra water and energy demand."
              : "These percentages describe snow risk, not how many resorts will close. Business models, adaptation and local terrain also matter."}
          </p>
          <ShareTools chart="snow-risk" params={{ snowScope: "europe" }} />
        </article>
        <article className={s.panel}>
          <h3>Even +2°C is not felt equally.</h3>
          <p className={s.small}>
            Very high snow-supply risk by region · same adaptation selection
          </p>
          {REGIONAL_SKI_RISK.map((row) => (
            <div className={s.riskRow} key={row.name}>
              <div className={s.riskLabel}>
                <span>{row.name}</span>
                <strong>
                  {row.name === "French Alps" && snowmaking === "natural"
                    ? "~"
                    : ""}
                  {row[snowmaking]}%
                </strong>
              </div>
              <div
                className={s.riskTrack}
                role="img"
                aria-label={`${row.name}: ${row[snowmaking]} per cent at very high snow risk`}
              >
                <div
                  className={s.riskBar}
                  data-adapted={snowmaking === "snowmaking"}
                  style={{ width: `${row[snowmaking]}%` }}
                />
              </div>
            </div>
          ))}
          <div className={s.riskAxis} aria-hidden="true"><span>0%</span><span>50%</span><span>100% of resorts</span></div>
          <DataTable
            headers={["Region at +2°C", "No snowmaking", "50% snowmaking"]}
            rows={REGIONAL_SKI_RISK.map((r) => [
              r.name,
              `${r.natural === 33 ? "~" : ""}${r.natural}%`,
              `${r.snowmaking}%`,
            ])}
          />
          <Sources ids={["regions"]} />
          <ShareTools chart="snow-risk" params={{ snowScope: "regional" }} />
          <p className={s.small}>
            The French Alps estimate without snowmaking is approximately one
            third. Regional averages conceal differences between resorts.
          </p>
        </article>
        <article className={`${s.panel} ${s.panelWide}`}>
          <h3>How high for a reliable natural-snow winter?</h3>
          <p className={s.small}>
            2030–2050 · modelled regional elevations, metres above sea level
          </p>
          <RangeChart
            rows={ELEVATIONS}
            max={3000}
            unit="m"
            selected={elevation}
            onSelect={setElevation}
            label="Projected natural-snow reliability elevations"
          />
          <p className={s.caption} aria-live="polite">
            {ELEVATIONS[elevation].name}:{" "}
            {ELEVATIONS[elevation].low.toLocaleString("en-GB")}–
            {ELEVATIONS[elevation].high.toLocaleString("en-GB")} m
          </p>
          <p className={s.small}>
            Groomed natural snow meeting a 100-day criterion in seven winters
            out of ten. Ranges span regional medians across three emissions
            scenarios, not uncertainty percentiles. They are not universal
            viability cut-offs.
          </p>
          <DataTable
            headers={["Region", "Elevation range (m)"]}
            rows={ELEVATIONS.map((r) => [r.name, `${r.low}–${r.high}`])}
          />
          <Sources ids={["elevation"]} />
          <ShareTools chart="snowline" />
        </article>
      </div>
      <div className="mt-9">
        <h3>Read the mountain, not just the resort name.</h3>
        <div className={s.altitude}>
          <div>
            <svg
              viewBox="0 0 450 245"
              className={s.mountain}
              role="img"
              aria-label="Conceptual mountain elevation bands, not a map or projected snowline"
            >
              <defs>
                <clipPath id="climate-mountain">
                  <path d="M20 224 145 56 191 112 254 20 431 224Z" />
                </clipPath>
              </defs>
              <g clipPath="url(#climate-mountain)">
                <path fill="#d4dcd3" d="M0 0H450V245H0Z" />
                <rect
                  x="0"
                  y="155"
                  width="450"
                  height="90"
                  fill={altitude === "low" ? "#16808f" : "#aabdae"}
                />
                <rect
                  x="0"
                  y="95"
                  width="450"
                  height="60"
                  fill={altitude === "mid" ? "#16808f" : "#becdc2"}
                />
                <rect
                  x="0"
                  y="0"
                  width="450"
                  height="95"
                  fill={altitude === "high" ? "#16808f" : "#e3ebe5"}
                />
              </g>
              <path
                d="M48 155H417M91 95H360"
                stroke="#758982"
                strokeDasharray="4 5"
              />
              <text x="336" y="148" fontSize="12" fill="#16233b">
                1,500 m
              </text>
              <text x="310" y="89" fontSize="12" fill="#16233b">
                2,000 m
              </text>
            </svg>
            <p className={s.small}>
              Conceptual Alpine bands. Select an elevation to explore the
              pressures.
            </p>
          </div>
          <div>
            <Segments
              label="Alpine elevation band"
              options={ALTITUDES}
              value={altitude}
              onChange={setAltitude}
            />
            <div className={s.regionSelected} aria-live="polite">
              <h3>{currentAltitude.title}</h3>
              <p className={`${s.small} mt-3`}>{currentAltitude.text}</p>
            </div>
            <Sources ids={["mountains"]} />
          </div>
        </div>
      </div>
      <div className={`${s.regionText} mt-8`}>
        <Segments
          label="World ski region"
          options={REGIONS}
          value={region}
          onChange={setRegion}
        />
        <div className={s.regionSelected} aria-live="polite">
          <h3>{currentRegion.title}</h3>
          <p>{currentRegion.text}</p>
        </div>
        <Sources ids={[currentRegion.source]} />
      </div>
    </>
  );
}

const WAVE_REGIONS = [
  {
    value: "atlantic",
    label: "North Atlantic",
    arrow: "↘",
    title: "Lower mean wave heights",
    text: "A projected decline in average significant wave height under high emissions. Storms still occur, and season, swell direction and local wind decide whether a session works.",
    direction: -1,
  },
  {
    value: "southern",
    label: "Southern Ocean",
    arrow: "↗",
    title: "Higher mean wave heights",
    text: "An increase in wave activity can change swell reaching exposed southern coasts. More offshore energy does not automatically produce better or safer surf at a particular break.",
    direction: 1,
  },
  {
    value: "pacific",
    label: "Tropical eastern Pacific",
    arrow: "↗",
    title: "Higher mean wave heights",
    text: "The regional projection points upwards under high emissions. Local exposure, reef and beach shape, and swell direction still control the waves people ride.",
    direction: 1,
  },
  {
    value: "med",
    label: "Mediterranean",
    arrow: "↘",
    title: "Lower mean wave heights",
    text: "Average wave heights are projected to decrease, while higher sea level still increases coastal pressure. Mean wave climate and storm extremes are different questions.",
    direction: -1,
  },
];

function SurfOutlook() {
  const [region, setRegion] = useClimateState(
    "waveRegion",
    "atlantic",
    WAVE_REGIONS.map((r) => r.value),
  );
  const [coral, setCoral] = useClimateState("coral", "1.5", ["1.5", "2"]);
  const wave = WAVE_REGIONS.find((r) => r.value === region)!;
  return (
    <div className={s.twoCol}>
      <article className={s.panel}>
        <h3>More warming does not mean more surf.</h3>
        <p className={s.small}>
          Regional direction of mean wave-height change · late-century high
          emissions, RCP8.5
        </p>
        <Segments
          label="Ocean wave region"
          options={WAVE_REGIONS}
          value={region}
          onChange={setRegion}
        />
        <svg
          className={s.waveDiagram}
          viewBox="0 0 450 140"
          role="img"
          aria-label={`Conceptual waves: ${wave.title.toLowerCase()}. This diagram shows direction of change, not wave height.`}
        >
          <path
            d="M0 70 Q28 5 56 70T168 70T280 70T392 70T504 70"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            style={{ transform: `scaleY(${wave.direction > 0 ? 1 : 0.5})` }}
          />
          <path d="M0 100H450" stroke="currentColor" strokeOpacity=".2" />
        </svg>
        <p className={s.diagramCaption}>Illustration of direction only, not a wave-height forecast.</p>
        <div aria-live="polite">
          <div className={s.waveArrow}>{wave.arrow}</div>
          <h3>{wave.title}</h3>
          <p className={s.small}>{wave.text}</p>
        </div>
        <Sources ids={["waveRegions"]} />
        <p className={s.note}>
          A multi-model study finds regional mean height and period changes of
          5–15% and direction shifts of 5–15° under high emissions. These are
          regional changes of either sign, not a 15% improvement in surfing.
        </p>
        <Sources ids={["waves"]} />
      </article>
      <article className={s.panel}>
        <div className={s.chartHead}>
          <h3>
            Half a degree matters
            <br />
            to a living reef.
          </h3>
          <Segments
            label="Coral warming level"
            options={[
              { value: "1.5", label: "+1.5°C" },
              { value: "2", label: "+2°C" },
            ]}
            value={coral}
            onChange={setCoral}
          />
        </div>
        <div className={s.number} aria-live="polite">
          {coral === "1.5" ? "70–90%" : ">99%"}
        </div>
        <p className={s.small}>
          Projected further decline in warm-water coral reefs
        </p>
        <div
          className={s.waffle}
          role="img"
          aria-label={
            coral === "1.5"
              ? "70 to 90 of 100 units of coral reefs projected to decline at 1.5 degrees"
              : "More than 99 of 100 units projected to decline at 2 degrees"
          }
        >
          {Array.from({ length: 100 }, (_, i) => (
            <span
              key={i}
              data-lost={i < (coral === "1.5" ? 70 : 99)}
              data-uncertain={coral === "1.5" ? i >= 70 && i < 90 : i === 99}
            />
          ))}
        </div>
        <div className={s.legend}>
          <span>
            <i className={s.dot} style={{ background: "#16808f" }} />
            Remaining
          </span>
          <span>
            <i className={s.dot} style={{ background: "#c7cecd" }} />
            Decline
          </span>
          <span>Hatched: range / remainder</span>
        </div>
        <p className={s.note}>
          For reef breaks: coral decline threatens the living ecosystem and
          coastal protection. It does not translate directly into the percentage
          of surf breaks lost.
        </p>
        <p className={`${s.small} mt-4`}>
          Global warming relative to pre-industrial levels; reef decline
          relative to the 2018 assessment’s present-day baseline. The grid
          represents projected reef decline, not a map of individual reefs.
        </p>
        <Sources ids={["coral"]} />
        <ShareTools chart="coral" />
      </article>
      <div className={`${s.panel} ${s.panelWide}`}>
        <h3>From offshore swell to the wave you ride.</h3>
        <p className={s.small}>
          A regional climate signal passes through local conditions before it
          reaches your board.
        </p>
        <ol className={s.surfJourney} aria-label="How offshore changes affect a surf session">
          <li>
            <strong>Swell arriving</strong>
            <p>Height, period and direction change the energy reaching a coast.</p>
          </li>
          <li>
            <strong>Wind and water level</strong>
            <p>Local wind shapes the surface. Tide and sea level set the water depth.</p>
          </li>
          <li>
            <strong>The break and access</strong>
            <p>Reefs, sandbanks and coastal shape determine how waves break and where you can enter.</p>
          </li>
        </ol>
        <p className={s.note}>
          For a trip or session, check the local swell, wind, tide and access
          forecast. Higher average offshore waves alone do not establish better
          surfing conditions.
        </p>
        <Sources ids={["waves", "tides"]} />
        <div className={s.source}>
          <a href="https://coastalscience.noaa.gov/science-areas/climate-change/wemo/" target="_blank" rel="noreferrer">
            NOAA · wind, depth and breaking waves ↗
          </a>
        </div>
        <div className={s.surfEcosystems}>
        <h3>The knock-on effects reach beyond the line-up.</h3>
        <div className={`${s.threeCol} mt-5`}>
          <div>
            <h3>Fish move. Habitats change.</h3>
            <p className={s.small}>
              Warming and oxygen loss alter marine habitats. Fish populations
              respond to temperature, fishing pressure and management, so
              temperature alone cannot forecast a local catch.
            </p>
          </div>
          <div>
            <h3>Reefs protect coasts.</h3>
            <p className={s.small}>
              Coral loss affects biodiversity and coastal protection. Reef
              structure and sea-level rise can change how waves break, alongside
              impacts on diving and local livelihoods.
            </p>
          </div>
          <div>
            <h3>Pollution is also local.</h3>
            <p className={s.small}>
              Rainfall, runoff and wastewater infrastructure affect bathing
              water. A warmer sea is not a direct measure of sewage or bacterial
              pollution; check local water-quality evidence before entering.
            </p>
          </div>
        </div>
        <Sources ids={["oceanLife", "tides"]} />
        </div>
      </div>
    </div>
  );
}

export function SportOutlook() {
  const [sport, setSport] = useClimateState("sport", "ski", ["ski", "surf"]);
  return (
    <section className={s.section} id="sports">
      <div className={s.wrap}>
        <SectionTitle
          number="01 / What it means for your sport"
          title={
            sport === "ski"
              ? "Where will winter still feel like winter?"
              : "The future of the waves we ride."
          }
          controls={
            <Segments
              label="Sport outlook"
              options={[
                { value: "ski", label: "△ Ski & snow" },
                { value: "surf", label: "≈ Surf & sea" },
              ]}
              value={sport}
              onChange={setSport}
            />
          }
        >
          Published regional projections make the global numbers tangible.
          Explore the differences that matter on the mountain and at the coast.
        </SectionTitle>
        <div hidden={sport !== "ski"}>
          <SkiOutlook />
        </div>
        <div hidden={sport !== "surf"}>
          <SurfOutlook />
        </div>
        <div className={s.readingLinks}>
          <Link href="/climate/skiing">
            The future of skiing: read the evidence
          </Link>
          <Link href="/climate/surfing">
            How climate change affects surfing
          </Link>
        </div>
      </div>
    </section>
  );
}
